import React, { memo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';

import Button from 'modules/views/shared/Button';
import Modal from 'modules/views/shared/Modal';
import CapturePhoto from 'modules/views/shared/CapturePhoto';
import Image from 'modules/views/shared/Image';
import { ReactComponent as CameraIcon } from 'assets/icons/Capture.svg';
import { ReactComponent as GalleryIcon } from 'assets/icons/PlusCertificateIcon.svg';
import { isMobileDevice } from 'utils/generalUtils';

import { useStyles } from './styles';

const ImagePickerDemo = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Detect if device is mobile/tablet (including iPad and Android tablets)
  const isMobileOrTablet = isMobileDevice({
    iPadAsMobile: true,
    androidTabletAsMobile: true,
  });

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleGalleryClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCameraClick = useCallback(() => {
    if (isMobileOrTablet) {
      // On mobile, use native camera capture
      // Used native camera to test locally
      cameraInputRef.current?.click();
    } else {
      // On desktop, use webcam modal
      setShowCaptureModal(true);
    }
  }, [isMobileOrTablet]);

  const handleCameraCapture = useCallback((imageSrc: string | string[]) => {
    const imageSource = Array.isArray(imageSrc) ? imageSrc[0] : imageSrc;
    setSelectedImage(imageSource);
    setShowCaptureModal(false);
  }, []);

  const handleClearImage = useCallback(() => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedImage) {
      setIsLoading(true);
      const type = selectedImage.substring(selectedImage.indexOf(':') + 1, selectedImage.indexOf(';'));
      // Expected type examples: "image/png", "image/jpeg", "image/jpg"
      console.log('Submitting image of type:', type);
      // Simulate upload delay
      setTimeout(() => {
        setIsLoading(false);
        alert('Image uploaded successfully!');
      }, 2000);
    }
  }, [selectedImage]);

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <h1 className={classes.title}>Image Picker Demo</h1>
        <p className={classes.subtitle}>
          {isMobileOrTablet
            ? 'Pick an image from gallery or capture from camera'
            : 'Select from device storage or capture from camera'}
        </p>

        {/* Image Preview */}
        {selectedImage && (
          <div className={classes.imagePreviewContainer}>
            <Image src={selectedImage} alt="Selected" className={classes.imagePreview} />
            <button className={classes.clearButton} onClick={handleClearImage} aria-label="Clear image">
              ✕
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className={classes.buttonsContainer}>
          <>
            <Button
              label={isMobileOrTablet ? 'Pick from Gallery' : 'Select from Device Storage'}
              variant="outlined"
              styleClass={classes.actionButton}
              onClick={handleGalleryClick}
              disabled={isLoading}
            />
            <Button
              label="Capture from Camera"
              variant="contained"
              styleClass={classes.actionButton}
              onClick={handleCameraClick}
              disabled={isLoading}
            />
          </>
        </div>

        {/* Hidden File Input for Gallery */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Hidden File Input for Camera (Mobile Only) */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          capture="environment"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Submit Button */}
        {selectedImage && (
          <div className={classes.submitContainer}>
            <Button
              label="Upload Image"
              variant="contained"
              isLoading={isLoading}
              onClick={handleSubmit}
              styleClass={classes.submitButton}
            />
          </div>
        )}

        {/* Device Info (for demo purposes) */}
        <div className={classes.deviceInfo}>
          <p className={classes.deviceInfoText}>
            <strong>Device Type:</strong> {isMobileOrTablet ? 'Mobile/Tablet' : 'Desktop/Laptop'}
          </p>
          <p className={classes.deviceInfoText}>
            <strong>Camera Method:</strong> {isMobileOrTablet ? 'Native Camera Capture' : 'Webcam Modal'}
          </p>
        </div>
      </div>

      {/* Camera Capture Modal (Desktop Only) */}
      {showCaptureModal && !isMobileOrTablet && (
        <CapturePhoto
          isProceedLoading={isLoading}
          onClose={() => setShowCaptureModal(false)}
          onProceed={handleCameraCapture}
        />
      )}
    </div>
  );
};

export default memo(ImagePickerDemo);
